import {useEffect} from 'react';
import {css} from '@emotion/css';
import Modal from 'patient-ping-remedy/packages/modal';
import Button, {StyleType} from 'patient-ping-remedy/packages/button';
import FileInput from 'patient-ping-remedy/packages/file-input';
import Checkbox from 'patient-ping-remedy/packages/checkbox';
import Spacer from 'patient-ping-remedy/packages/spacer';
import {createFormFieldConfig, useForm} from '@/hooks/useForm';
import {StyledHintTypography, StyledSpacer} from '@clients/profile_page/forms_tab/FormUploadModal.styles';
import {Required} from '@clients/profile_page/forms_tab/FormExpandMoreButton.styles';
import Validate from '@/helpers/validate';

type Props = {
  isOpen: boolean;
  isPending: boolean;
  dischargeOnly?: boolean;
  close(): void;
  confirm(formData: FormData): void;
}

export default function UploadModal({ isOpen, close, confirm, isPending, dischargeOnly }: Props) {
  const { form, isValid, setInitialForm, handleChange, handleSubmit } = useForm(() => {
    const formData = new FormData();
    formData.append('file', form.file.value[0]);
    confirm(formData);
  });

  useEffect(() => {
    if (isOpen) {
      setInitialForm({
        ...createFormFieldConfig(
          'file',
          [],
          { valid: false, message: '' },
          [Validate.validateFile(52400000, dischargeOnly ? ['application/pdf'] : undefined)],
          true,
        ),
        ...(dischargeOnly ? createFormFieldConfig(
          'attestation',
          false,
          { valid: false, message: '' },
          [Validate.boolean],
          true,
        ) : {}),
      });
    }
  }, [isOpen]);

  return (
    <Modal
      isOpen={isOpen}
      headerText="Upload attachment"
      buttons={[
        <Button key="cancel" styleType={StyleType.TERTIARY} onClick={close}>Cancel</Button>,
        <Button key="upload" disabled={!isValid() || isPending} onClick={handleSubmit}>Upload</Button>
      ]}
      className={css({ width: '60rem', fontSize: '16px' })}
    >
      <FileInput
        acceptedFiles={dischargeOnly ? '.pdf' : '*'}
        label="Select a file"
        onChange={(files: File[]) => handleChange(null, 'file', files)}
        value={form.file?.value ?? []}
        buttonText="Browse"
        buttonType={StyleType.SECONDARY}
        error={form.file?.valid.message}
      />
      {dischargeOnly && (
        <Checkbox
          className={css({ marginTop: '2rem' })}
          key="attestation"
          onChange={(checked) => handleChange(null, 'attestation', checked)}
          value={form.attestation?.value}
          label={(
            <>
              <Required>* </Required>
              <Spacer itemWidth={'0.5rem'}>
              </Spacer>I am attesting that the uploaded attachment is a Discharge Instruction
            </>
          ) as unknown as string}
        />
      )}
      <StyledSpacer />
      <StyledHintTypography>
        By uploading documents here, I acknowledge and agree that I have performed
        back-ups of such documents and am not relying solely on Bamboo Health to store
        and maintain such documents.  Further, I acknowledge and agree that Bamboo
        Health is not responsible for any loss of data or documentation, including
        but not limited to, accidental deletion, hardware failure, software
        malfunction, or any other unforeseen circumstances.
      </StyledHintTypography>

      <StyledHintTypography>
        I agree that I am using this platform to aid in process transparency and
        general communication across care team members. This platform does not replace
        existing review, approval, or submission workflows.
      </StyledHintTypography>
    </Modal>
  );
}
